import { PaymentMethod, PaymentStatus } from "generated-graphql/graphql";
import * as yup from "yup";
import { safeParseFloat } from "./numberUtils";
import { PayeeFormInputs, PaymentType } from "./types";

// Factory function to create validation schema with payment context
export const createValidationSchema = (
  paymentId?: string,
  existingPayment?: any
) =>
  yup.object({
    status: yup
      .string()
      .nullable()
      .test("disable-editing-based-on-status", function (this) {
        const { status } = this.parent as PayeeFormInputs;

        if (status === PaymentStatus.Pending) {
          return this.createError({
            message: `Editing is disabled because the payment is currently in "${status}" status.`,
          });
        }

        return true;
      }),
    tenantId: yup
      .string()
      .nullable()
      .test("tenant-required", function (this) {
        // Skip validation for existing payments (they already have a tenant)
        if (paymentId && existingPayment) {
          return true;
        }

        // For new payments, tenant is required
        const { tenantId } = this.parent as PayeeFormInputs;
        if (!tenantId || tenantId.trim() === "") {
          return this.createError({
            message: "Organization selection is required",
            path: "tenantId",
          });
        }

        return true;
      }),
    type: yup.string().nullable().required("Select a payment type"),
    payeeType: yup.string().nullable().required("Required"),
    recipientName: yup
      .string()
      .nullable()
      .required("Required")
      .when("type", {
        is: PaymentType.LOB_CHECK,
        then: yup
          .string()
          .nullable()
          .required("Required")
          .max(40, "Recipient cannot be more than 40 characters"),
      }),
    companyName: yup
      .string()
      .nullable()
      .when("type", {
        is: PaymentType.LOB_CHECK,
        then: yup
          .string()
          .nullable()
          .max(40, "Company cannot be more than 40 characters"),
      }),
    addressLine1: yup
      .string()
      .nullable()
      .when("type", {
        is: PaymentType.LOB_CHECK,
        then: yup
          .string()
          .nullable()
          .required("Required")
          .max(64, "Address 1 cannot be more than 64 characters")
          .test(
            "address-combined-length",
            "Combined address lines must not exceed 50 characters",
            function () {
              const { addressLine1, addressLine2 } = this
                .parent as PayeeFormInputs;
              const combinedLength =
                (addressLine1?.length || 0) + (addressLine2?.length || 0);
              return combinedLength <= 50;
            }
          ),
      }),
    addressLine2: yup
      .string()
      .nullable()
      .when("type", {
        is: PaymentType.LOB_CHECK,
        then: yup
          .string()
          .nullable()
          .max(64, "Address 2 cannot be more than 64 characters"),
      }),
    city: yup
      .string()
      .nullable()
      .when("type", {
        is: PaymentType.LOB_CHECK,
        then: yup.string().nullable().required("Required"),
      }),
    state: yup
      .string()
      .nullable()
      .when("type", {
        is: PaymentType.LOB_CHECK,
        then: yup.string().nullable().required("Required"),
      }),
    zip: yup
      .string()
      .nullable()
      .when("type", {
        is: PaymentType.LOB_CHECK,
        then: yup.string().nullable().required("Required"),
      }),
    memo: yup
      .string()
      .nullable()
      .when("type", {
        is: PaymentType.LOB_CHECK,
        then: yup
          .string()
          .nullable()
          .max(40, "Memo cannot be more than 40 characters"),
      }),
    invoiceNumber: yup.string().nullable(),
    paymentMethod: yup
      .string()
      .nullable()
      .required("Required")
      .when("type", {
        is: PaymentType.LOB_CHECK,
        then: yup
          .string()
          .nullable()
          .test(
            "is-check",
            "Payment method must be Check",
            (value) => value === PaymentMethod.Check
          ),
      }),
    amount: yup
      .number()
      .nullable()
      .required("Required")
      .min(0.01, "Payment amount must be greater than $0")
      .test(
        "matches-sum-of-items",
        "Total amount must equal the sum of all item amounts",
        function (this, totalAmount) {
          const { associations } = this.parent as PayeeFormInputs;

          if (associations && associations.length) {
            // Multiply by 100 to avoid floating point errors
            const factor = 100;

            // Sum all associations - no duplicate detection needed
            const intSumOfItems = associations.reduce(
              (sum: number, item: any) =>
                sum +
                Math.round((safeParseFloat(String(item.amount)) ?? 0) * factor),
              0
            );
            return totalAmount === intSumOfItems / factor;
          }

          return true;
        }
      ),
    associations: yup.array().of(
      yup.object({
        amount: yup.number().nullable().required("Required"),
        reportId: yup.string().nullable(),
        facilityId: yup.string().nullable(),
      })
    ),
    paymentInvoices: yup
      .array()
      .of(
        yup.object({
          id: yup.string().nullable(),
          description: yup.string().nullable(),
          invoiceDocumentId: yup.string().nullable(),
          invoiceDocument: yup.object().nullable(),
          invoiceNumber: yup.string().nullable(),
        })
      )
      .test(
        "sufficient-invoices",
        "An invoice is required",
        function (formInvoices) {
          const { status } = this.parent as PayeeFormInputs;

          // Skip validation for pending payments
          if (status === PaymentStatus.Pending) {
            return true;
          }

          // Existing payments: documents are read-only, no form validation needed
          if (paymentId) {
            return true;
          }

          // New payments: validate form has at least one invoice for all payment types
          const result = (formInvoices?.length || 0) >= 1;
          return result;
        }
      )
      .when("type", {
        is: PaymentType.LOB_CHECK,
        then: yup
          .array()
          .max(1, "Only one invoice is allowed for Lob payments"),
      }),
    receiptDocument: yup.object().nullable(),
    activityId: yup.string().nullable(),
    requeueJob: yup.boolean().nullable(),
    billable: yup
      .boolean()
      .nullable()
      .required("Please select a billing status"),
    comment: yup
      .string()
      .nullable()
      .when("billable", {
        is: false,
        then: yup
          .string()
          .nullable()
          .required(
            "Exception note is required when marking payment as not billable"
          ),
      }),
  });

// Default export for backward compatibility
export const validationSchema = createValidationSchema();
